import { useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { aiDisabledCopy } from '@/api/errorCopy';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { AiJobFailure } from '@/features/ai/AiJobFailure';
import { meetingsApi, type MeetingHome, type MeetingSummary } from '@/api/meetings';
import { AiPill } from '@/components/AiPill';
import { HeadedCard } from '@/components/HeadedCard';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { ItemRow } from './ItemRow';
import type { ItemAddress } from './itemAddress';

interface PersonOption {
  userId: number;
  displayName: string;
}

interface SessionSummariesProps {
  virtualAccountId: MeetingHome;
  meetingId: number;
  occurrenceKey: string;
  summaries: MeetingSummary[];
  canWrite: boolean;
  /**
   * The signed-in person may ask for an AI summary: the virtual account's owner (AI is owner-only,
   * TMMAPI-0146), or a standalone meeting's organizer, who pays for it (TMMAPI-0188 U2).
   */
  organizer?: boolean;
  address: ItemAddress;
  people: PersonOption[];
  onChanged: () => void;
}

/**
 * The session's AI summaries and their proposed items (a card with a header, TMMAPP-0103). A
 * summary is a proposal a human confirms (MM-D-45); the recordings that feed it are their own card
 * (`SessionRecordings`).
 */
export function SessionSummaries({
  virtualAccountId,
  meetingId,
  occurrenceKey,
  summaries,
  canWrite,
  organizer = false,
  address,
  people,
  onChanged,
}: SessionSummariesProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const showSummarize = organizer && me.user.aiEnabled;
  const showAiOff = organizer && !me.user.aiEnabled;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletRefusal, setWalletRefusal] = useState<{
    refusal: AiRefusal & { reason: AiWalletReason };
    kind: 'THIS_MEETING' | 'ROLLING';
  } | null>(null);
  const [inserted, setInserted] = useState<MeetingSummary[]>([]);
  const shown = [
    ...summaries,
    ...inserted.filter((row) => !summaries.some((existing) => existing.id === row.id)),
  ];

  async function request(summaryKind: 'THIS_MEETING' | 'ROLLING') {
    setError(null);
    setWalletRefusal(null);
    setPending(true);
    try {
      const created = await meetingsApi.requestSummary(
        virtualAccountId,
        meetingId,
        occurrenceKey,
        summaryKind,
      );
      const done =
        created.status === 'PENDING'
          ? await meetingsApi.pollSummary(virtualAccountId, meetingId, occurrenceKey, created.id)
          : created;
      setInserted((prev) => [done, ...prev.filter((row) => row.id !== done.id)]);
      // A served reason reads on the summary's own row (TMMAPI-0152); an older failure, here too.
      if (done.status === 'FAILED' && done.failureReason === null) {
        setError(t('meetings.summaryFailed'));
      }
      onChanged();
    } catch (err) {
      const refusal = aiRefusal(err);
      if (isApiError(err) && err.code === ErrorCodes.SUMMARY_NO_INPUT)
        setError(t('meetings.summaryNoInput'));
      else if (isAiWalletRefusal(refusal)) setWalletRefusal({ refusal, kind: summaryKind });
      else if (refusal !== null) setError(aiDisabledCopy(t, refusal));
      else if (isApiError(err) && err.code === ErrorCodes.AI_BUDGET_EXCEEDED)
        setError(t('ai.capped'));
      else setError(t('meetings.errorGeneric'));
    } finally {
      setPending(false);
    }
  }

  // Ask again for a failed summary's kind: the owner only, where AI is on.
  const retryFor = (kind: 'THIS_MEETING' | 'ROLLING' | null) =>
    kind === null ? undefined : () => void request(kind);

  return (
    <HeadedCard
      title={t('workspace.summaries')}
      subtitle={t('meetings.session.summariesSub')}
      id="session-summaries"
    >
      {canWrite && (
        <Stack direction="row" sx={{ gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {showSummarize && (
            <>
              <Button size="small" onClick={() => void request('THIS_MEETING')}>
                {t('meetings.summarizeThis')}
              </Button>
              <Button size="small" onClick={() => void request('ROLLING')}>
                {t('meetings.summarizeRolling')}
              </Button>
            </>
          )}
          {showAiOff && <Typography variant="body2">{t('ai.reason.USER')}</Typography>}
        </Stack>
      )}
      {pending && (
        <Skeleton
          variant="rounded"
          height={80}
          sx={{ mb: 2 }}
          role="status"
          aria-label={t('meetings.summaryPending')}
        />
      )}
      {error !== null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {walletRefusal !== null && (
        <Box sx={{ mb: 2 }}>
          <AiWalletPrompt
            refusal={walletRefusal.refusal}
            onTryAgain={() => void request(walletRefusal.kind)}
          />
        </Box>
      )}
      <Stack spacing={2}>
        {shown.map((summary) => (
          <Box key={summary.id}>
            <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* TMM-B9: the summaries endpoint is model-produced; the pill marks a summary's text. */}
              {summary.status === 'READY' && <AiPill />}
              <Typography variant="caption" color="text.secondary">
                {[
                  summary.kind ? t(`meetings.summaryKind.${summary.kind}`) : null,
                  t(`meetings.summaryStatus.${summary.status}`),
                  summary.createdAt ? formatInstant(summary.createdAt, locale, zone) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Stack>
            {summary.status === 'PENDING' && <Skeleton variant="text" />}
            {summary.status === 'FAILED' &&
              (summary.failureReason === null ? (
                <Typography>{t('meetings.summaryFailed')}</Typography>
              ) : (
                // The owner asked for it on their own wallet; any other member reads the reason only.
                <Box sx={{ mt: 0.5 }}>
                  <AiJobFailure
                    failureReason={summary.failureReason}
                    firstPurchaseRequired={summary.firstPurchaseRequired}
                    debtMinor={summary.debtMinor}
                    walletOwner={organizer}
                    fallback={t('meetings.summaryFailed')}
                    onRetry={showSummarize ? retryFor(summary.kind) : undefined}
                  />
                </Box>
              ))}
            {summary.status === 'READY' && (
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{summary.body}</Typography>
            )}
            {summary.proposedItems.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                address={address}
                // An AI summary's proposed items are confirmed or rejected by the owner only (0146).
                canWrite={canWrite && organizer}
                people={people}
                onChanged={onChanged}
              />
            ))}
          </Box>
        ))}
        {shown.length === 0 && (
          <Typography variant="body2">{t('meetings.session.summariesEmpty')}</Typography>
        )}
      </Stack>
    </HeadedCard>
  );
}
